import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWallet } from "@/context/WalletContext";
import { Switch } from "@/components/ui/switch";
import { Wallet, Twitter, Send, MessageCircle, Loader2, Zap } from "lucide-react";

const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-line bg-surface p-5">
    <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-dust">{title}</h2>
    {children}
  </div>
);

export default function SettingsPage() {
  const { wallet, walletType, user, setUser } = useWallet();
  const [f, setF] = useState({ display_name: "", default_voice: "degen", notify_email: "", rpc_url: "", socials: { x: "", telegram: "", discord: "" }, auto_refill: { enabled: false, threshold: 25, amount: 149, currency: "USDC" } });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setF({
      display_name: user.display_name || "", default_voice: user.default_voice || "degen",
      notify_email: user.notify_email || "", rpc_url: user.rpc_url || "",
      socials: { x: user.socials?.x || "", telegram: user.socials?.telegram || "", discord: user.socials?.discord || "" },
      auto_refill: {
        enabled: user.auto_refill?.enabled ?? false,
        threshold: user.auto_refill?.threshold ?? 25,
        amount: user.auto_refill?.amount ?? 149,
        currency: user.auto_refill?.currency ?? "USDC",
      },
    });
  }, [user]);

  const setAR = (patch) => setF((s) => ({ ...s, auto_refill: { ...s.auto_refill, ...patch } }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.post("/settings", f); setUser(data); toast.success("Settings saved."); }
    catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-8 lg:px-8">
      <h1 className="mb-6 text-2xl font-bold tracking-tight">Settings</h1>
      <div className="space-y-5">
        <Card title="Wallet & security">
          <div className="flex items-center gap-3 rounded-lg bg-raised/40 p-3">
            <Wallet size={18} className="text-juice" />
            <div><p className="font-mono text-sm text-cream">{wallet}</p><p className="text-xs text-dust">Non-custodial. You sign every launch.</p></div>
          </div>
        </Card>

        <Card title="Profile">
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs text-dust">Display name</Label><Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} placeholder="satoshi" className="bg-raised border-line" data-testid="settings-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-dust">Default agent voice</Label>
              <div className="grid grid-cols-3 gap-2">
                {["degen", "clean", "hype"].map((v) => (
                  <button key={v} onClick={() => setF({ ...f, default_voice: v })} className={`rounded-lg border px-3 py-2 text-sm capitalize transition-colors ${f.default_voice === v ? "border-juice bg-juice/10 text-cream" : "border-line text-dust hover:bg-raised"}`}>{v}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-dust">Notification email</Label><Input value={f.notify_email} onChange={(e) => setF({ ...f, notify_email: e.target.value })} placeholder="you@mail.com" className="bg-raised border-line" data-testid="settings-email" /></div>
          </div>
        </Card>

        <Card title="Connected socials">
          <div className="space-y-3">
            {[{ k: "x", icon: Twitter, ph: "@handle" }, { k: "telegram", icon: Send, ph: "t.me/group" }, { k: "discord", icon: MessageCircle, ph: "discord.gg/invite" }].map(({ k, icon: Icon, ph }) => (
              <div key={k} className="flex items-center gap-3">
                <Icon size={16} className="text-ai" />
                <Input value={f.socials[k]} onChange={(e) => setF({ ...f, socials: { ...f.socials, [k]: e.target.value } })} placeholder={ph} className="bg-raised border-line" data-testid={`settings-social-${k}`} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Auto-refill">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap size={16} className="text-pulp" />
                <div>
                  <p className="text-sm text-cream">Keep campaigns funded</p>
                  <p className="text-xs text-dust">Top up automatically when prepaid fuel runs low.</p>
                </div>
              </div>
              <Switch checked={f.auto_refill.enabled} onCheckedChange={(v) => setAR({ enabled: v })} data-testid="autorefill-toggle" />
            </div>
            {f.auto_refill.enabled && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5"><Label className="text-xs text-dust">When below ($)</Label>
                  <Input type="number" min="5" value={f.auto_refill.threshold} onChange={(e) => setAR({ threshold: parseFloat(e.target.value) || 0 })} className="bg-raised border-line font-mono" data-testid="autorefill-threshold" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-dust">Add ($)</Label>
                  <Input type="number" min="5" value={f.auto_refill.amount} onChange={(e) => setAR({ amount: parseFloat(e.target.value) || 0 })} className="bg-raised border-line font-mono" data-testid="autorefill-amount" /></div>
                <div className="space-y-1.5"><Label className="text-xs text-dust">Pay with</Label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {["USDC", "USDT", "SOL", "ETH"].map((c) => (
                      <button key={c} onClick={() => setAR({ currency: c })} className={`rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${f.auto_refill.currency === c ? "border-juice bg-juice/10 text-juice" : "border-line text-dust hover:bg-raised"}`} data-testid={`autorefill-currency-${c}`}>{c}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {f.auto_refill.enabled && (
              <p className="text-xs text-dust/70">
                {walletType === "demo"
                  ? "Demo wallet: refills apply instantly (simulated)."
                  : "Connected wallet: you'll get a signing prompt to approve each refill — we never move funds without your signature."}
              </p>
            )}
          </div>
        </Card>

        <Card title="Advanced">
          <div className="space-y-1.5"><Label className="text-xs text-dust">Custom RPC URL (optional)</Label><Input value={f.rpc_url} onChange={(e) => setF({ ...f, rpc_url: e.target.value })} placeholder="https://…" className="bg-raised border-line font-mono" data-testid="settings-rpc" /></div>
        </Card>

        <Button onClick={save} disabled={saving} className="gap-2 bg-juice text-ink hover:bg-juice/90 font-semibold" data-testid="settings-save">
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} Save settings
        </Button>
      </div>
    </div>
  );
}
